import angular from "angular";

import { AuthorizedAccounts, AuthorizedAvatar, CompanyAccount, PersonAccount, Translations, Configs } from "@salaxy/core";

import { BaseService } from "./BaseService";
import { SessionService } from "./SessionService";

/** TODO: Move the data model and data to server-side. */
type Partner = {
  /** Name of the partner service */
  title?: string,
  /** Unique identifier */
  id: string,
  /** Palkkaus.fi account ID (IBAN) that is authorized. */
  accountId: string,
  /** Square logo image */
  img: string,
  /** Status text: Short description */
  status?: string,
  /** If true, the integration is enabled. */
  enabled?: boolean,
};

/**
 * CRUD functionality for the authorized accounts.
 * Additionally listing of authorizing accounts.
 */
export class AuthorizedAccountService extends BaseService<AuthorizedAvatar> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$rootScope", "SessionService", "AuthorizedAccounts"];

  /**
   * String that identifies the service event (onChange/notify).
   * Must be unique for the service class.
   */
  protected eventPrefix = "authorizedAccount";

  private authorizingAccounts: (PersonAccount | CompanyAccount)[] = [];

  /** Intial data owner */
  private initialAuthorizingAccountsOwner: string;

  private currentPartnerSite: any;

  /**
   * List of Software/Integration partner sites
   */
  private partnerIntegrationSiteList: Partner[] = [
    {
      id: "askelma",
      accountId: "FI14POYA0028382282",
      img: "https://cdn.salaxy.com/img/salaxy/customer/askelma/logo.png",
    },
    {
      id: "easoft",
      accountId: "FI11POY10030958375",
      img: "https://cdn.salaxy.com/img/salaxy/customer/easoft/logo.png",
    },
    {
      id: "domus",
      accountId: "FI23POY10006460809",
      img: "https://cdn.salaxy.com/img/salaxy/customer/domus/logo.png",
    },
    {
      id: "holvi",
      accountId: "FI24POYH0021937564",
      img: "https://cdn.salaxy.com/img/salaxy/customer/holvi/logo.png",
    },
    {
      id: "isolta",
      accountId: "FI24POYI0018540478",
      img: "https://cdn.salaxy.com/img/salaxy/customer/isolta/logo.svg",
    },
    {
      id: "finago",
      accountId: "FI15POYA0008369224",
      img: "https://cdn.salaxy.com/img/salaxy/customer/finago-solo/logo.png",
    },
    {
      id: "accountor",
      accountId: "FI03POYA0003689675",
      img: "https://cdn.salaxy.com/img/salaxy/customer/accountor-go/logo.png",
    },
    {
      id: "briox",
      accountId: "FI33POYB0024403824",
      img: "https://cdn.salaxy.com/img/salaxy/customer/briox/logo.png",
    },
    {
      id: "nocfo",
      accountId: "FI45POYN0031497697",
      img: "https://cdn.salaxy.com/img/salaxy/customer/nocfo/logo.png",
    },
    {
      id: "passeliMerit",
      accountId: "FI43POY10010435606",
      img: "https://cdn.salaxy.com/img/salaxy/customer/passelimerit/logo.png",
    },
    {
      id: "ecom",
      accountId: Configs.current?.isTestData === true ? "FI11POYE0009680831" : "FI44POY10009680831",
      img: "https://cdn.salaxy.com/img/salaxy/customer/ecom/logo.png",
    }
  ];

  constructor(
    $rootScope: angular.IRootScopeService,
    sessionService: SessionService,
    private authorizedAccountsApi: AuthorizedAccounts,
  ) {
    super($rootScope, sessionService, authorizedAccountsApi);

    this.refreshAuthorizingAccounts();

    sessionService.subscribe($rootScope, () => {
      this.refreshAuthorizingAccounts();
    });

    this.partnerIntegrationSiteList.forEach((x) => {
      x.title = Translations.get(`SALAXY.NG1_Services.AuthorizedAccountService.partnerIntegrationSiteList.${x.id}.title`);
      x.status = Translations.get(`SALAXY.NG1_Services.AuthorizedAccountService.partnerIntegrationSiteList.${x.id}.description`);
    });
  }

  /**
   * Refreshes existing authorizing accounts.
   * @returns A Promise with result data (PersonAccount|CompanyAccount)[]
   */
  public reloadAuthorizingAccounts(): Promise<(PersonAccount | CompanyAccount)[]> {
    return this.authorizedAccountsApi.getAuthorizingAccounts().then((data) => this.authorizingAccounts = data);
  }

  /**
   * Lists all accounts on behalf of which this account has been authorized to act.
   * @returns A Promise with result data (PersonAccount|CompanyAccount)[]
   */
  public getAuthorizingAccounts(): (PersonAccount | CompanyAccount)[] {
    return this.authorizingAccounts;
  }

  /**
   * Sets the current partner site
   * @param partnerSite A partner site to be set as current
   */
  public setCurrentPartnerSite(partnerSite: any): void {
    this.currentPartnerSite = partnerSite;
  }

  /**
   * Returns the current partner site
   */
  public getCurrentPartnerSite(): any {
    return this.currentPartnerSite;
  }

  /**
   * Return a list of all possible Integration partner sites
   *
   * Partner sites are services that have Salaxy or Palkkaus functionality in them. Others are services that have a different sign-in method,
   * and these services need an authorization to access user's Salaxy/Palkkaus account. Others use the Salaxy/Palkkaus sign-in method, so they don't
   * need separate authorization. Due to some services needing an authorization and others not needing it, all partner services need to be stored in a
   * separate property, simply listing authorizations does not suffice.
   *
   * Partner services that need an authorization have a property accountId, that is the IBAN for their Salaxy acccount
   * @returns List of partner site objects.
   */
  public getPartnerIntegrationSiteList(): any {
    this.partnerIntegrationSiteList.forEach((x) => x.enabled = !!this.list.find((y) => y.id === x.accountId));
    return this.partnerIntegrationSiteList;
  }

  /**
   * Returns the partner site with given id
   * @param id The name of the partner site
   * @returns Partner site object with the given id, or undefined.
   */
  public getPartnerSite(id): any {
    const sites = this.getPartnerIntegrationSiteList().filter((x) => x.id === id);
    return sites.length > 0 ? sites[0] : null;
  }

  private refreshAuthorizingAccounts() {
    if (this.sessionService.isSessionChecked && this.sessionService.isAuthenticated) {
      const owner = this.sessionService.session.currentAccount.owner;
      if (this.initialAuthorizingAccountsOwner != owner){
        this.authorizingAccounts = [];
        this.initialAuthorizingAccountsOwner = owner;
        this.reloadAuthorizingAccounts();
      }
    } else {
      this.authorizingAccounts = [];
      this.initialAuthorizingAccountsOwner = null;
    }
  }
}
